import { AsYouType, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/mobile';

import { dayjs } from '@/utils/dayjsSetup.js';

import { useUserLinkingStore } from '@/store.js';

/**
 * Removes all whitespace characters (spaces, tabs, newlines) from a string.
 *
 * @param {string | null | undefined} value - The input string
 * @returns {string | undefined} String with all whitespace removed, or undefined if input is nullish
 *
 * @example
 * removeWhiteSpaces('+49 151 1234 5678')  // '+4915112345678'
 * removeWhiteSpaces('  hello  world  ')   // 'helloworld'
 * removeWhiteSpaces(null)                 // undefined
 */
export const removeWhiteSpaces = (value) => {
    return value?.replace(/\s+/g, '');
};

/**
 * Normalizes a phone number string to E.164 format.
 * Uses libphonenumber-js for parsing — returns a result for any parseable input
 * without checking full validity, so partially valid numbers may also be normalized.
 * Intended for saving phone numbers to the API.
 *
 * @param {string | null | undefined} phone - Phone number in any common format
 * @returns {string} Phone number in E.164 format, or empty string if input is falsy/unparseable
 *
 * @example
 * normalizePhoneNumber('+49 151 1234 5678')   // '+4915112345678'
 * normalizePhoneNumber('+49 (0)151 23456789') // '+4915123456789'
 * normalizePhoneNumber('+48 504 220 231')     // '+48504220231'
 * normalizePhoneNumber('+4915112345678')      // '+4915112345678' (already E.164 — idempotent)
 * normalizePhoneNumber('0151 12345678')       // '' (no country code — cannot parse)
 * normalizePhoneNumber('')                    // ''
 * normalizePhoneNumber(null)                  // ''
 */
export const normalizePhoneNumber = (phone) => {
    if (!phone) {
        return '';
    }
    const phoneNumber = parsePhoneNumberFromString(phone);
    if (!phoneNumber) {
        return '';
    }
    return phoneNumber.number;
};

/**
 * Validates that a phone number is a valid mobile number in international format.
 * Requires the '+' prefix — numbers without a country code are rejected.
 * Uses libphonenumber-js/mobile which only recognizes mobile number ranges.
 *
 * @param {string | null | undefined} phone - Phone number to validate (must start with '+')
 * @returns {boolean} True if the number is a valid mobile number
 *
 * @example
 * // Valid mobile numbers
 * isMobilePhoneNumber('+49 151 12345678')  // true  (German mobile)
 * isMobilePhoneNumber('+48 504 220 231')   // true  (Polish mobile)
 * isMobilePhoneNumber('+44 7911 123456')   // true  (UK mobile)
 *
 * // Landlines — valid phone numbers but not mobile
 * isMobilePhoneNumber('+49 30 12345678')   // false (Berlin landline)
 * isMobilePhoneNumber('+49 89 12345678')   // false (Munich landline)
 *
 * // Invalid or missing format
 * isMobilePhoneNumber('0151 12345678')     // false (no '+' prefix)
 * isMobilePhoneNumber('+49 151')           // false (too short)
 * isMobilePhoneNumber('')                  // false
 * isMobilePhoneNumber(null)                // false
 */
export const isMobilePhoneNumber = (phone) => {
    if (!phone || !phone.startsWith('+')) {
        return false;
    }
    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed) {
        return false;
    }
    if (!isValidPhoneNumber(phone)) {
        return false;
    }
    return parsed.getType() === 'MOBILE';
};

export const buildFormApiBody = ({
    signatureData,
    formType,
    formTemplateId,
    currentFormTemplateId,
    isConsentAllForms,
    isUserLinked,
    user,
    professional,
    formData,
    insuranceType,
    insuranceCompany,
    insuranceId,
    additionalData,
}) => {
    const { code, locationKey } = useUserLinkingStore.getState();
    const isSelfPayer = insuranceType === 'SELFPAYER';

    const api_body = {
        instances: [{ type: formType, id: formTemplateId || currentFormTemplateId || '' }],
        signature_data: signatureData,
        consent: true,
        ...additionalData,
    };

    if (isConsentAllForms) api_body.document_id = currentFormTemplateId;

    if (isUserLinked) {
        api_body.linked_form_code = code;
        api_body.location_id = locationKey;
    }

    if (user) {
        api_body.user_id = user.id;
        api_body.professional = professional;
        api_body.personal_data = {
            health_insurance: { insurance_type: insuranceType, insurance_company: insuranceCompany },
        };
    }

    if (!user) {
        api_body.personal_data = {
            professional: professional,
            personal: {
                title: formData?.title,
                first_name: formData?.firstName.trim(),
                middle_name: formData?.middleName?.trim(),
                last_name: formData?.lastName.trim(),
                date_of_birth: dayjs(formData?.dob, 'DD.MM.YYYY', true).isValid()
                    ? dayjs(formData?.dob, 'DD.MM.YYYY').format('YYYY-MM-DD')
                    : formData?.dob,
                email: formData?.email,
                phone: formData?.phoneNo?.number ? normalizePhoneNumber(formData.phoneNo.number) : undefined,
                gender: formData?.gender || '',
            },
            contact: {
                street: formData?.street,
                house_number: formData?.houseNumber,
                zip: formData?.zipCode,
                city: formData?.city,
                country_code: formData?.country?.code,
            },
            health_insurance: {
                insurance_type:
                    insuranceType?.toUpperCase() === 'SELFPAYER' ? 'SELF_PAYER' : insuranceType?.toUpperCase(),
                insurance_company: insuranceCompany || '',
                ...(!isSelfPayer && {
                    insurance_id: insuranceId || '',
                }),
            },
        };
    }

    return api_body;
};

/**
 * Splits a phone number into its country calling code and national number.
 * More lenient than `normalizePhoneNumber` — handles incomplete numbers and legacy formats.
 * Intended for populating UI fields (separate country code dropdown + number input).
 *
 * @param {string | null | undefined} phone - Phone number in any format
 * @returns {{ code: string, number: string } | undefined} Parsed phone object, or undefined if input is falsy
 *
 * @example
 * // Fully valid international numbers
 * normalizePhone('+49 151 12345678')   // { code: '+49', number: '15112345678' }
 * normalizePhone('+48504220231')       // { code: '+48', number: '504220231' }
 *
 * // Incomplete numbers (user still typing) — returns best-effort split
 * normalizePhone('+49')               // { code: '+49', number: '' }
 * normalizePhone('+')                 // { code: '+', number: '' }
 *
 * // Legacy local formats — defaults to German country code (+49)
 * normalizePhone('0151 12345678')     // { code: '+49', number: '015112345678' }
 *
 * // Falsy input
 * normalizePhone('')                  // undefined
 * normalizePhone(null)                // undefined
 */
export const normalizePhone = (phone) => {
    if (!phone) return undefined;

    const cleaned_phone = removeWhiteSpaces(phone);

    // Parse as international number
    const parsed = parsePhoneNumberFromString(cleaned_phone);
    if (parsed) {
        // Successfully parsed - return code and national number
        return {
            code: `+${parsed.countryCallingCode}`,
            number: parsed.nationalNumber,
        };
    }

    // Fallback for incomplete international numbers (e.g., "+49" while user is typing)
    if (cleaned_phone.startsWith('+')) {
        const formatter = new AsYouType();
        formatter.input(cleaned_phone);
        const callingCode = formatter.getCallingCode();

        if (callingCode) {
            return {
                code: `+${callingCode}`,
                number: cleaned_phone.substring(callingCode.length + 1),
            };
        }
        // Just "+" or very incomplete input
        return { code: cleaned_phone, number: '' };
    }

    // Final fallback for old phone formats (e.g., "0151 12345678")
    // Display them so users can see and update, but validation will fail
    if (cleaned_phone) {
        return { code: '+49', number: cleaned_phone };
    }

    return undefined;
};

/**
 * Convenience wrapper around `normalizePhone` — returns a single concatenated string
 * instead of a `{ code, number }` object. Falls back to the original input if unparseable.
 *
 * @param {string | null | undefined} phone - Phone number in any format
 * @returns {string | null | undefined} Concatenated code + number (e.g. '+4915112345678'), or original input if unparseable
 *
 * @example
 * normalizePhoneToString('+49 151 12345678')  // '+4915112345678'
 * normalizePhoneToString('0151 12345678')     // '+49015112345678'
 * normalizePhoneToString(null)                // null (returns input as-is)
 */
export const normalizePhoneToString = (phone) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return phone;

    return normalized.code + normalized.number;
};

/**
 * Validates an email address against RFC 5322 simplified regex.
 * Returns false for any non-string input.
 *
 * @param {string | null | undefined} email - Email address to validate
 * @returns {boolean} True if the email matches a valid format
 *
 * @example
 * isEmail('user@example.com')        // true
 * isEmail('name+tag@domain.co.uk')   // true
 * isEmail('invalid@')                // false
 * isEmail('')                        // false
 */
export const isEmail = (email) => {
    if (typeof email !== 'string') return false;
    const regex =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // source: https://emailregex.com/
    return regex.test(email);
};
